import {
  FormControl,
  FormHelperText,
  Select,
  Skeleton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import PhoneInput2 from 'react-phone-input-2';

import 'react-phone-input-2/lib/style.css';

import MultiSelectWithCheckbox from '../MultiSelectCheckbox/MultiSelectCheckbox';

import './FormInput.scss';

const ErrorHelper = ({ errors, name }) =>
  errors?.[name] ? <FormHelperText>{errors[name].message}</FormHelperText> : null;

// textual form-controls—like inputs, passwords, textareas etc.
const TextualInput = ({
  label,
  type,
  fieldType,
  name,
  placeholder,
  errors,
  control,
  startIcon,
  startIconEle,
  endIcon,
  endIconEle,
  endIconClassName,
  register,
  className,
  labelClassName,
  containerClassName,
  refCallback,
  isLoading,
  required,
  ...otherProps
}) => {
  return (
    <FormControl fullWidth className={containerClassName} error={errors && errors[name]}>
      <Typography variant='formlabel' component='p' mb={1} className={labelClassName}>
        {label}
        {required && <span style={{ color: '#d32f2f', marginLeft: 2 }}>*</span>}
      </Typography>
      {isLoading ? (
        <Skeleton variant='rectangular' height={40} sx={{ borderRadius: 2.5 }} />
      ) : (
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              id={name}
              type={type}
              placeholder={placeholder}
              fullWidth
              className={className}
              ref={(r) => refCallback?.(r)}
              error={errors && errors[name]}
              slotProps={{
                input: {
                  startAdornment: startIcon && startIconEle ? startIconEle : null,
                  endAdornment: endIcon && endIconEle ? endIconEle : null,
                },
              }}
              {...otherProps}
            />
          )}
        />
      )}
      <ErrorHelper errors={errors} name={name} />
    </FormControl>
  );
};

// handle select controls
const SelectInput = ({
  label,
  type,
  fieldType,
  name,
  placeholder,
  errors,
  control,
  startIcon,
  startIconEle,
  endIcon,
  endIconEle,
  endIconClassName,
  register,
  className,
  labelClassName,
  containerClassName,
  refCallback,
  children,
  required,
  ...otherProps
}) => {
  return (
    <FormControl fullWidth className={containerClassName} error={errors && errors[name]}>
      <Typography variant='formlabel' component='p' mb={1} className={labelClassName}>
        {label}
        {required && <span style={{ color: '#d32f2f', marginLeft: 2 }}>*</span>}
      </Typography>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          if (fieldType === 'multi-select-checkbox') {
            return (
              <MultiSelectWithCheckbox
                {...field}
                options={otherProps.options || []}
                value={field.value || []}
                onChange={(selected) => {
                  field.onChange(selected);
                  otherProps?.onChange && otherProps?.onChange(selected);
                }}
                id={name}
                placeholder={placeholder}
                className={`${className} ${errors && errors[name] ? 'error' : ''}`}
                ref={(r) => refCallback?.(r)}
                {...otherProps}
              />
            );
          }
          return (
            <Select
              {...field}
              id={name}
              placeholder={placeholder}
              className={className}
              displayEmpty={otherProps?.displayEmpty ?? true}
              ref={(r) => refCallback?.(r)}
              error={errors && errors[name]}
              {...otherProps}
            >
              {children}
            </Select>
          );
        }}
      />
      <ErrorHelper errors={errors} name={name} />
    </FormControl>
  );
};

// non-textual checkbox
const CheckInput = ({
  label,
  type,
  fieldType,
  name,
  placeholder,
  errors,
  control,
  startIcon,
  startIconEle,
  endIcon,
  endIconEle,
  endIconClassName,
  register,
  className,
  labelClassName,
  containerClassName,
  refCallback,
  required,
  ...otherProps
}) => {
  return (
    <FormControl fullWidth className={containerClassName} error={errors && errors[name]}>
      <Typography variant='formlabel' component='p' mb={1} className={labelClassName}>
        {label}
        {required && <span style={{ color: '#d32f2f', marginLeft: 2 }}>*</span>}
      </Typography>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Switch
            {...field}
            checked={field.value} // important: bind checked
            onChange={(e) => field.onChange(e.target.checked)}
            id={name}
            placeholder={placeholder}
            className={className}
            ref={(r) => refCallback?.(r)}
            error={errors && errors[name]}
            {...otherProps}
          />
        )}
      />
      <ErrorHelper errors={errors} name={name} />
    </FormControl>
  );
};

const PhoneInput = ({
  label,
  type,
  fieldType,
  name,
  placeholder,
  errors,
  control,
  startIcon,
  startIconEle,
  endIcon,
  endIconEle,
  endIconClassName,
  register,
  className,
  labelClassName,
  containerClassName,
  refCallback,
  defaultValue,
  data_testid = 'phone-input-field',
  inputProps = {},
  required,
  ...otherProps
}) => {
  return (
    <FormControl
      fullWidth
      className={'phone-input-container ' + containerClassName}
      error={errors && errors[name]}
    >
      <Typography variant='formlabel' component='p' mb={1} className={labelClassName}>
        {label}
        {required && <span style={{ color: '#d32f2f', marginLeft: 2 }}>*</span>}
      </Typography>

      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange } }) => (
          <PhoneInput2
            country={value?.country || defaultValue}
            value={value?.fullNumber || 0}
            onChange={(phone, countryData) => {
              const formatted = `+${countryData.dialCode}${phone?.replace(
                new RegExp(`^${countryData.dialCode}`),
                ''
              )}`;
              onChange({
                fullNumber: formatted,
                phone: phone?.replace(new RegExp(`^${countryData.dialCode}`), ''),
                dialCode: `+${countryData.dialCode}`,
                country: countryData.countryCode,
              });
            }}
            inputClass='mui-phone-input'
            buttonClass='mui-phone-flag'
            containerClass='mui-phone-container'
            inputProps={{
              name: name,
              required: required,
              autoFocus: false,
              ...inputProps,
            }}
            className={`phone-input ${className}`}
            data_testid={data_testid}
            {...otherProps}
          />
        )}
      />
      <ErrorHelper errors={errors[name]} name={'fullNumber'} />
      <ErrorHelper errors={errors[name]} name={'country'} />
    </FormControl>
  );
};

const INPUT_MAP = {
  text: TextualInput,
  select: SelectInput,
  check: CheckInput,
  phone: PhoneInput,
  'multi-select-checkbox': SelectInput,
};

const FormInput = ({
  label,
  fieldType,
  type,
  name,
  placeholder,
  startIcon,
  startIconEle,
  endIcon,
  endIconEle,
  endIconClassName,
  register,
  errors,
  control,
  className,
  labelClassName,
  containerClassName,
  refCallback,
  children,
  ...otherProps
}) => {
  const sharedProps = {
    label,
    type,
    fieldType,
    name,
    placeholder,
    endIcon,
    endIconEle,
    endIconClassName,
    register,
    errors,
    control,
    className,
    labelClassName,
    containerClassName,
    refCallback,
    startIcon,
    startIconEle,
    ...otherProps,
  };

  const Component = INPUT_MAP[fieldType];

  if (!Component) return null;
  return <Component {...sharedProps}>{children}</Component>;
};

export default FormInput;
